import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import './GraphicPreview.css';
import api from '../services/api';

const WIDTH = 1080;
const HEIGHT = 1080;
const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicPreview({
  graphicData,
  teamSport,           // kept for API compatibility — not used for sport emojis
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,        // explicit px override; null = auto (IIFE calc)
  accentBlockColor = null,    // explicit color override; null = teamColors.secondary
  dominantImageScale = null,  // 10–300; null = 100 (full size)
  dominantImageX = null,      // 0–100; null = 50 (centered)
  dominantImageY = null,      // 0–100; null = 50 (centered)
  dominantImageOpacity = 100,
  detailTextColor = null,     // explicit color; null = defaults per element
  dateFormat = 'full',
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;
  const primary = teamColors.primary;
  const secondary = teamColors.secondary;
  const hasImage = Boolean(dominantImage);

  // Text right boundary: leave room for right-side dominant image
  const textPanelRight = hasImage ? 600 : 60;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${primary} 0%, ${secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${primary} 0%, ${secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${primary} 50%, ${secondary} 50%)`;
      case 'solid-primary':
        return primary;
      case 'solid-secondary':
        return secondary;
      default:
        return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
    }
  };

  // Dynamic team name font size — tighter when dominant image compresses the text panel
  const teamNameSize = (name) => {
    const len = (name || '').length;
    if (hasImage) {
      if (len <= 8) return 60;
      if (len <= 12) return 48;
      if (len <= 16) return 38;
      return 30;
    }
    if (len <= 8) return 80;
    if (len <= 12) return 66;
    if (len <= 16) return 52;
    if (len <= 20) return 42;
    return 34;
  };

  const homeNameSize = teamNameSize(homeTeam);
  const awayNameSize = teamNameSize(awayTeam);
  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const autoHeroFontSize = (() => {
    const text = 'GAME DAY';
    const sizes = [180, 160, 140, 120, 100];
    for (const size of sizes) {
      if (text.length * size * 0.62 < WIDTH - 80) return size;
    }
    return 100;
  })();
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const displayHeroFontSize = heroFontSize != null ? Number(heroFontSize) : autoHeroFontSize;
  const accentColor = accentBlockColor || secondary;
  const scale = (dominantImageScale ?? 100) / 100;

  return (
    <div
      className="graphic-preview"
      id="graphic-preview"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        background: getBackground(),
        fontFamily: "'Bebas Neue', 'Arial Narrow', Arial, sans-serif",
      }}
    >
      {/* ── LAYER 1 — Background photo (full bleed, optional) ── */}
      {backgroundPhoto && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: WIDTH,
              height: HEIGHT,
              overflow: 'hidden',
              zIndex: 1,
            }}
          >
            <img
              src={backgroundPhoto}
              alt=""
              crossOrigin="anonymous"
              style={{
                position: 'absolute',
                top: `${bgImageY}%`,
                left: `${bgImageX}%`,
                transform: `translate(-50%, -50%) scale(${bgImageScale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
              }}
            />
          </div>
          {/* Dark scrim for readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: WIDTH,
              height: HEIGHT,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.28) 50%, rgba(0,0,0,0.82) 100%)',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* ── LAYER 2 — Diagonal color-block accent (secondary, lower-right) ── */}
      {/* Two rotated rectangles create layered depth — no clip-path (breaks html2canvas) */}
      <div
        style={{
          position: 'absolute',
          bottom: -280,
          right: -280,
          width: 960,
          height: 960,
          background: accentColor,
          transform: 'rotate(28deg)',
          zIndex: 2,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -400,
          right: 40,
          width: 700,
          height: 700,
          background: 'rgba(0,0,0,0.15)',
          transform: 'rotate(28deg)',
          zIndex: 2,
        }}
      />

      {/* ── LAYER 3 — Dominant image (optional hero, right side) ── */}
      {dominantImage && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: 1080,
            height: 1080,
            overflow: 'visible',
            zIndex: 3,
          }}
        >
          <img
            src={dominantImage}
            alt=""
            style={{
              position: 'absolute',
              top: `${dominantImageY ?? 50}%`,
              left: `${dominantImageX ?? 50}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity: (dominantImageOpacity ?? 100) / 100,
              height: 1080 * scale,
              width: 'auto',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* ── LAYER 4 — Text hierarchy ── */}

      {/* Hero: GAME DAY — Bebas Neue, fills ~85% card width */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 60,
          right: 60,
          zIndex: 5,
          overflow: 'visible',
          fontFamily: heroFontFamily,
          fontSize: displayHeroFontSize,
          lineHeight: 0.88,
          letterSpacing: 10,
          color: '#ffffff',
          whiteSpace: 'nowrap',
          textShadow: '0 4px 40px rgba(0,0,0,0.75)',
        }}
      >
        GAME DAY
      </div>

      {/* Accent stripe below hero */}
      <div
        style={{
          position: 'absolute',
          top: 282,
          left: 60,
          width: 120,
          height: 6,
          background: secondary,
          zIndex: 4,
        }}
      />

      {/* Sub-hero: Home team — logo + name */}
      <div
        style={{
          position: 'absolute',
          top: 312,
          left: 60,
          right: textPanelRight,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          overflow: 'hidden',
        }}
      >
        {homeTeamLogo && (
          <img
            src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
            alt={homeTeam}
            crossOrigin="anonymous"
            style={{
              height: 72,
              width: 'auto',
              maxWidth: 120,
              flexShrink: 0,
            }}
          />
        )}
        <div
          style={{
            fontSize: homeNameSize,
            lineHeight: 1,
            color: detailTextColor || '#ffffff',
            letterSpacing: 3,
            textShadow: '0 2px 24px rgba(0,0,0,0.8)',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {homeTeam || 'YOUR TEAM'}
        </div>
      </div>

      {/* VS */}
      <div
        style={{
          position: 'absolute',
          top: 420,
          left: 60,
          zIndex: 4,
          fontSize: 56,
          lineHeight: 1,
          letterSpacing: 6,
          color: detailTextColor || secondary,
          textShadow: '0 2px 16px rgba(0,0,0,0.7)',
        }}
      >
        VS
      </div>

      {/* Away team — logo + name */}
      <div
        style={{
          position: 'absolute',
          top: 504,
          left: 60,
          right: textPanelRight,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          overflow: 'hidden',
        }}
      >
        {awayTeamLogoPreview && (
          <img
            src={awayTeamLogoPreview}
            alt={awayTeam}
            crossOrigin="anonymous"
            style={{
              height: 72,
              width: 'auto',
              maxWidth: 120,
              flexShrink: 0,
            }}
          />
        )}
        <div
          style={{
            fontSize: awayNameSize,
            lineHeight: 1,
            color: detailTextColor || '#ffffff',
            letterSpacing: 3,
            textShadow: '0 2px 24px rgba(0,0,0,0.8)',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {awayTeam || 'OPPONENT'}
        </div>
      </div>

      {/* Divider rule */}
      <div
        style={{
          position: 'absolute',
          top: 694,
          left: 60,
          width: hasImage ? 380 : 440,
          height: 3,
          background: 'rgba(255,255,255,0.25)',
          zIndex: 4,
        }}
      />

      {/* Supporting: date, time, location — Barlow Condensed, no emojis */}
      <div
        style={{
          position: 'absolute',
          top: 718,
          left: 60,
          right: textPanelRight,
          zIndex: 4,
          fontFamily:
            "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif",
          fontSize: 40,
          fontWeight: 600,
          color: detailTextColor || 'rgba(255,255,255,0.78)',
          letterSpacing: 1,
          lineHeight: 1.5,
        }}
      >
        {formatGameDate(gameDate, dateFormat) && <div>{formatGameDate(gameDate, dateFormat)}</div>}
        {gameTime && <div>{formatGameTime(gameTime)}</div>}
        {location && <div>{location}</div>}
      </div>

      {/* ── LAYER 5 — Team logos are embedded in Layer 4 rows above ── */}

      {/* ── LAYER 6 — Watermark (free tier only, bottom-right) ── */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 6,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicPreview;
